/*
 * Copyright (c) 2022. Revo Digital
 * ---
 * Author: gabriele
 * File: CropImageDialog.tsx
 * Project: mle-client-n
 * Committed last: 2022/2/13 @ 1316
 * ---
 * Description:
 */

import { observer }                                 from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Classes,
  Dialog,
  Divider,
  Intent,
  Spinner
}                                                   from '@blueprintjs/core';
import classNames                                   from 'classnames';
import {
  currentThemeIsDark,
  dataURLFromBase64
}                                                   from '../../../common/utils';
import {
  Cropper
}                                                   from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import {
  ObservableImage
} from '../../../store/observables/ObservableImage';
import {
  mleDomainGqlClient
} from '../../../api/domain';
import {
  IMAGE_BY_ID
} from '../../../api/domain/queries.gql';
import {
  Popover2
} from '@blueprintjs/popover2';
import {
  BaseDialogProps,
  HasEditor
} from '../../../common/props';

interface Props extends HasEditor, BaseDialogProps {
  asset: ObservableImage;
  assetId: number;
}

export const CropImageDialog = observer((props: Props) => {
  const cropperRef = useRef<HTMLImageElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cropData, setCropData] = useState<Cropper.Data>();
  const [imageContent, setImageContent] = useState('');

  const onCrop = useCallback((event: Cropper.CropEvent<HTMLImageElement>) => {
    const cropInfo = event.detail;
    setCropData(cropInfo);
  }, [setCropData]);

  const handleApply = useCallback(() => {
    const image = props.asset;

    if (image && cropData) {
      image.setCropHeight(cropData.height, { applyToPamelaShape: true });
      image.setCropWidth(cropData.width, { applyToPamelaShape: true });
      image.setCropX(cropData.x, { applyToPamelaShape: true });
      image.setCropY(cropData.y, { applyToPamelaShape: true });
    }
    props.onClose();
  }, [cropData, props.asset]);

  const handleApplyWithNewSize = useCallback(() => {
    const image = props.asset;

    if (image && cropData) {
      image.setCropHeight(cropData.height, { applyToPamelaShape: true });
      image.setCropWidth(cropData.width, { applyToPamelaShape: true });
      image.setCropX(cropData.x, { applyToPamelaShape: true });
      image.setCropY(cropData.y, { applyToPamelaShape: true });
      image.setWidth(cropData.width / 10, { applyToPamelaShape: true });
      image.setHeight(cropData.height / 10, { applyToPamelaShape: true });
    }
    props.onClose();
  }, [cropData, props.asset]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const response = await mleDomainGqlClient.query({
        query: IMAGE_BY_ID,
        variables: {
          id: props.assetId,
        }
      });

      setImageContent(dataURLFromBase64(response.data.image.content));

      setIsLoading(false);
    })();
  }, [setIsLoading, props.assetId]);

  const applyMenu = (
    <div className="p-2">
      <div className="mb-2">
        <Button
          small
          minimal
          intent={ Intent.PRIMARY }
          onClick={ handleApply }
        >
          Adatta a originale
        </Button>
      </div>
      <Button
        small
        minimal
        intent={ Intent.PRIMARY }
        onClick={ handleApplyWithNewSize }
      >
        Riproporziona
      </Button>
    </div>
  );

  const applyButtons = (<div className="flex flex-row justify-end space-x-1">
    <Button
      small
      onClick={ props.onClose }
    >
      Annulla
    </Button>
    <Popover2 position="top" content={ applyMenu }>
      <Button
        small icon="tick"
        intent={ Intent.PRIMARY }>
        Applica
      </Button>
    </Popover2>
  </div>);

  return <Dialog
    title="Ritaglia immagine"
    canEscapeKeyClose={ false }
    className={ classNames({ 'bp4-dark': currentThemeIsDark() }) }
    style={ { width: '50rem', height: '40rem' } }
    isOpen={ props.isOpen }
    onClose={ props.onClose }
    canOutsideClickClose={ false }
  >
    <div className={ Classes.DIALOG_BODY }>
      <div className="bp4-text-muted">
        Utilizza il rettangolo di selezione e lo zoom per ottenere le dimensioni
        desiderate
      </div>
      { applyButtons }
      <Divider className="my-3"/>
      <div className="w-full">
        {
          isLoading &&
          <div className="mx-auto"><Spinner size={ 25 }/></div>
        }
        <Cropper
          className="w-full mb-2"
          initialAspectRatio={ 16 / 9 }
          guides={ true }
          crop={ onCrop }
          ref={ cropperRef }
          src={ imageContent }
          ready={ () => setIsLoading(false) }
        />
      </div>
    </div>
  </Dialog>;
});
